import { type ErrorResponse, HttpTransport, type ITransportResponse, type TransportOptions } from '@cubejs-client/core';

const FORBIDDEN = 403;

const isForbidden = (result: Response | ErrorResponse): boolean => 'status' in result && result.status === FORBIDDEN;

interface TokenRefreshTransportOptions extends Pick<TransportOptions, 'apiUrl' | 'authorization'> {
  onRefreshToken: () => Promise<string>;
  onFreshTokenRejected?: () => void;
}

class TokenRefreshTransport extends HttpTransport {
  private readonly onRefreshToken: () => Promise<string>;
  private readonly onFreshTokenRejected: (() => void) | undefined;

  constructor(options: TokenRefreshTransportOptions) {
    const { onRefreshToken, onFreshTokenRejected, ...transportOptions } = options;

    super(transportOptions);

    this.onRefreshToken = onRefreshToken;
    this.onFreshTokenRejected = onFreshTokenRejected;
  }

  request(apiMethod: string, params: Record<string, unknown>): ITransportResponse<Response> {
    const inner = super.request(apiMethod, params);
    let hasRetried = false;

    return {
      subscribe: <CBResult>(
        callback: (result: Response | ErrorResponse, resubscribe: () => Promise<CBResult>) => CBResult,
      ) =>
        inner.subscribe<CBResult>((result, resubscribe) => {
          if (!isForbidden(result)) {
            return callback(result, resubscribe);
          }

          if (hasRetried) {
            this.onFreshTokenRejected?.();

            return callback(result, resubscribe);
          }

          hasRetried = true;

          return this.onRefreshToken().then((token) => {
            this.authorization = token;

            return resubscribe();
          }) as CBResult;
        }),
    };
  }
}

export { TokenRefreshTransport };
